import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import type { ListItemState } from './@types/ItemState'
import type { SliceListItems } from './@types/SliceItems'
import type { ListSliceMethods } from './@types/SliceMutators'
import { DEFAULT_LIST_ITEMS } from './constants'
import { mutateListItem } from './mutateItem'

type ListSliceConfig = {
  name: string
}

export function createListsSlice<Entity extends { id: string }, Filters>(config: ListSliceConfig) {
  type EntityState = ListItemState<Entity, Filters>
  type EntityMutators = ListSliceMethods<Entity, Filters>
  type EntitySlice = EntityMutators & SliceListItems<EntityState>

  return create<EntitySlice>()(
    devtools(
      (set, get) => ({
        items: DEFAULT_LIST_ITEMS,
        setItemLoadStart: (key, filters, pagination, sort, loaded = false) => {
          set(
            mutateListItem<EntityState>(
              key,
              Object.fromEntries(
                Object.entries({
                  loaded,
                  loading: true,
                  filters,
                  pagination,
                  sort,
                }).filter(([, value]) => value !== null),
              ),
            ),
            false,
            `${config.name}/setItemLoadStart`,
          )
        },
        setItemLoadSuccess: (key, items, meta) => {
          set(
            mutateListItem<EntityState>(key, {
              loaded: true,
              loading: false,
              error: undefined,
              items,
              meta,
            }),
            false,
            `${config.name}/setItemLoadSuccess`,
          )
        },
        setItemLoadFailure: (key, error) => {
          set(
            mutateListItem<EntityState>(key, {
              items: [],
              meta: undefined,
              loading: false,
              error,
            }),
            false,
            `${config.name}/setItemLoadFailure`,
          )
        },
        setItemLoaded: (key, loaded) => {
          set(mutateListItem<EntityState>(key, { loaded }), false, `${config.name}/setItemLoaded`)
        },
        getItem: key => {
          return get().items[key]
        },
      }),
      {
        name: config.name,
      },
    ),
  )
}
